import { zodResolver } from "@hookform/resolvers/zod";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import isValidABN from "is-valid-abn";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Button, ErrorMessage, Icon, Loading, LocationInput, Panel, TextInput, Typography } from "@/components/atoms";
import { GeneralLogo } from "@/components/organisms/General/GeneralLogo";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useLazyGetProviderQuery, useRegisterProviderWithKindeMutation } from "@/redux/apis/auth/authApi";
import { useAppDispatch } from "@/redux/hooks";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { addToast } from "@/utils/addToast";

const formSchema = z
  .object({
    businessName: z.string().min(1, "Field is required and can't be empty"),
    abn: z.string().min(1, "Field is required and can't be empty"),
    businessAddress: z.string().min(1, "Field is required and can't be empty"),
  })
  .refine((data) => isValidABN(data.abn), {
    message: "Field must be a valid ABN.",
    path: ["abn"],
  });

type FormData = z.infer<typeof formSchema>;

interface PlatformSetupStepProps {
  onSetupComplete: () => void;
}

export const PlatformSetupStep = ({ onSetupComplete }: PlatformSetupStepProps) => {
  const { user } = useKindeAuth();
  const dispatch = useAppDispatch();
  const [registerProvider, { isLoading: isSubmitting }] = useRegisterProviderWithKindeMutation();
  const [getProvider] = useLazyGetProviderQuery();
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors: formErrors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      businessName: "",
      abn: "",
      businessAddress: "",
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    if (!user) {
      addToast("danger", "User not authenticated");
      return;
    }

    if (isProcessing) {
      return; // Prevent double submission
    }

    setIsProcessing(true);

    try {
      const requestBody = {
        email: user.email || "",
        first_name: user.givenName || "",
        last_name: user.familyName || "",
        business_registration_type: "ABN" as const,
        business_registration_id: data.abn,
        business_name: data.businessName,
        business_address: data.businessAddress,
      };

      const result = await registerProvider(requestBody).unwrap();

      // Extract provider ID from response and fetch operator data
      const providerId = result.provider_uuid;

      if (providerId) {
        try {
          const operator = await getProvider(providerId).unwrap();
          dispatch(setActiveOperator(operator));
        } catch (error) {
          console.error("Failed to fetch operator data:", error);
        }
      }

      addToast("success", "Platform setup complete");
      onSetupComplete();
    } catch (error) {
      console.error("Registration error:", error);
      getErrorMessages(error as FetchBaseQueryError | SerializedError).forEach((m) => addToast("danger", m));
      setIsProcessing(false);
    }
  });

  return (
    <Panel className="relative h-fit w-[440px] p-8">
      {(isSubmitting || isProcessing) && <Loading className="rounded-lg" />}

      {/* Logo and Brand */}
      <div className="mb-8">
        <GeneralLogo />
      </div>

      {/* Title and Description */}
      <div className="mb-6">
        <Typography variant="h1" className="mb-2">
          Let&apos;s get you set up
        </Typography>
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          We just need a few basics to get you up and running.
        </Typography>
      </div>

      {/* Form */}
      <form onSubmit={onSubmit} className="flex flex-col gap-6">
        {/* Registered Business Name */}
        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Registered Business Name
          </Typography>
          <TextInput placeholder="Demo Co." maxLength={45} hasError={!!formErrors.businessName} {...register("businessName")} />
          <ErrorMessage errors={formErrors} name="businessName" />
        </div>

        {/* ABN */}
        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            ABN
          </Typography>
          <TextInput type="number" placeholder="12345678901" maxLength={16} hasError={!!formErrors.abn} {...register("abn")} />
          <ErrorMessage errors={formErrors} name="abn" />
        </div>

        {/* Registered Business Address */}
        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Registered Business Address
          </Typography>
          <Controller
            name="businessAddress"
            control={control}
            render={({ field }) => (
              <LocationInput
                placeholder="Search for your business address"
                renderIcon={() => <Icon name="Location" size="md" variant="Bold" className="text-primary" />}
                onLocationChange={(value) => {
                  // Always convert to string to avoid null values
                  field.onChange(value?.address || "");
                }}
                hasError={!!formErrors.businessAddress}
                allowManualInput={true}
                defaultValue={field.value}
              />
            )}
          />
          <ErrorMessage errors={formErrors} name="businessAddress" />
        </div>

        {/* Save Button */}
        <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting || isProcessing}>
          Save
        </Button>
      </form>
    </Panel>
  );
};
